'use client';

import { useEffect, useState, useCallback, useRef } from 'react';
import dynamic from 'next/dynamic';
import {
  MapPin,
  MagnifyingGlass,
  Spinner,
  CheckCircle,
  XCircle,
  CaretDown,
  X,
} from '@phosphor-icons/react';
import { useAddressStore } from '@/stores/address-store';
import { cn } from '@/lib/utils';
import type { AddressSuggestion } from '@/lib/api/delivery';

const YandexMapInner = dynamic(() => import('./YandexMapInner'), {
  ssr: false,
  loading: () => (
    <div className="absolute inset-0 flex items-center justify-center bg-[var(--bg-primary)] rounded-2xl">
      <div className="flex flex-col items-center gap-3">
        <Spinner className="w-8 h-8 text-[var(--primary)] animate-spin" />
        <span className="text-sm text-[var(--text-secondary)] font-medium">Загрузка карты...</span>
      </div>
    </div>
  ),
});

export function DeliveryMap() {
  const {
    selectedCity,
    cities,
    deliveryPoints,
    suggestions,
    lastCheckResult,
    isLoading,
    fetchCities,
    setSelectedCity,
    fetchDeliveryPoints,
    fetchSuggestions,
    selectSuggestion,
    clearSuggestions,
    checkAddress,
  } = useAddressStore();

  const [dataLoading, setDataLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showCityDropdown, setShowCityDropdown] = useState(false);
  const [checked, setChecked] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    const init = async () => {
      setDataLoading(true);
      await fetchCities();
      setDataLoading(false);
    };
    init();
  }, [fetchCities]);

  useEffect(() => {
    if (selectedCity) {
      fetchDeliveryPoints(selectedCity.id);
    }
  }, [selectedCity, fetchDeliveryPoints]);

  // searchPanel рендерится дважды (мобильная версия под картой + десктопный
  // оверлей), поэтому «клик мимо» нельзя проверять через ref: один и тот же ref
  // получают оба экземпляра, и живым остаётся последний в дереве — на втором
  // брейкпоинте выпадашка закрывалась бы на mousedown раньше, чем click дойдёт
  // до пункта списка. Ищем ближайшего предка по data-атрибуту — работает для
  // любого числа копий панели.
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target instanceof Element ? e.target : null;
      if (!target?.closest('[data-address-search]')) {
        setShowSuggestions(false);
      }
      if (!target?.closest('[data-city-select]')) {
        setShowCityDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleInputChange = useCallback(
    (value: string) => {
      setQuery(value);
      setChecked(false);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        if (value.length >= 3) {
          fetchSuggestions(value);
          setShowSuggestions(true);
        } else {
          clearSuggestions();
          setShowSuggestions(false);
        }
      }, 300);
    },
    [fetchSuggestions, clearSuggestions],
  );

  const handleSelectSuggestion = useCallback(
    async (suggestion: AddressSuggestion) => {
      setQuery(suggestion.value);
      setShowSuggestions(false);
      clearSuggestions();
      await selectSuggestion(suggestion);
      setChecked(true);
    },
    [selectSuggestion, clearSuggestions],
  );

  const handleCheck = useCallback(async () => {
    if (!query.trim()) return;
    setShowSuggestions(false);
    await checkAddress(query);
    setChecked(true);
  }, [query, checkAddress]);

  const handleClear = useCallback(() => {
    setQuery('');
    setChecked(false);
    clearSuggestions();
  }, [clearSuggestions]);

  const result = checked ? lastCheckResult : null;

  /* ====== Shared search panel (used in both mobile below-map and desktop overlay) ====== */
  const searchPanel = (
    <>
      {/* City selector */}
      <div className="mb-3" data-city-select>
        <div className="relative">
          <button
            type="button"
            onClick={() => setShowCityDropdown(!showCityDropdown)}
            className={cn(
              'w-full flex items-center justify-between px-3.5 py-2.5 rounded-xl',
              'bg-[var(--bg-primary)] border border-[var(--border-default)]',
              'text-left text-[var(--text-primary)] font-medium text-sm',
              'transition-colors duration-150 hover:border-[var(--border-hover)]',
              'focus:outline-none focus:ring-2 focus:ring-[var(--primary)]/30',
            )}
          >
            <span className="flex items-center gap-2">
              <MapPin weight="fill" className="w-4 h-4 text-[var(--primary)]" />
              {selectedCity?.name || 'Город'}
            </span>
            <CaretDown
              weight="bold"
              className={cn(
                'w-3.5 h-3.5 text-[var(--text-tertiary)] transition-transform duration-150',
                showCityDropdown && 'rotate-180',
              )}
            />
          </button>

          {showCityDropdown && cities.length > 0 && (
            <div className="absolute z-30 top-full mt-1.5 w-full rounded-xl bg-[var(--surface-primary)] border border-[var(--border-default)] shadow-xl overflow-hidden animate-fade-in">
              {cities.map((city) => (
                <button
                  key={city.id}
                  type="button"
                  onClick={() => {
                    setSelectedCity(city);
                    setShowCityDropdown(false);
                    setChecked(false);
                  }}
                  className={cn(
                    'w-full px-4 py-2.5 text-left text-sm transition-colors duration-100',
                    'hover:bg-[var(--surface-hover)]',
                    selectedCity?.id === city.id
                      ? 'text-[var(--primary)] font-bold bg-[var(--primary-subtle)]'
                      : 'text-[var(--text-primary)]',
                  )}
                >
                  {city.name}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Address input */}
      <div className="relative" data-address-search>
        <div className="relative flex gap-2">
          <div className="relative flex-1">
            <MagnifyingGlass
              weight="bold"
              className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-tertiary)]"
            />
            <input
              type="text"
              value={query}
              onChange={(e) => handleInputChange(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleCheck()}
              placeholder="Улица и номер дома..."
              className={cn(
                'w-full pl-10 pr-9 py-3 rounded-xl',
                'bg-[var(--bg-primary)] border border-[var(--border-default)]',
                'text-[var(--text-primary)] text-sm placeholder:text-[var(--text-tertiary)]',
                'transition-colors duration-150',
                'hover:border-[var(--border-hover)]',
                'focus:outline-none focus:ring-2 focus:ring-[var(--primary)]/30 focus:border-[var(--border-focus)]',
              )}
            />
            {query && (
              <button
                type="button"
                onClick={handleClear}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-[var(--surface-hover)] flex items-center justify-center hover:bg-[var(--surface-active)] transition-colors duration-100"
              >
                <X weight="bold" className="w-3 h-3 text-[var(--text-tertiary)]" />
              </button>
            )}
          </div>

          <button
            type="button"
            onClick={handleCheck}
            disabled={isLoading || !query.trim()}
            className={cn(
              'px-5 py-3 rounded-xl font-bold text-sm shrink-0',
              'bg-[var(--primary)] text-white',
              'transition-colors duration-150',
              'hover:bg-[var(--primary-hover)] active:bg-[var(--primary-active)]',
              'disabled:opacity-50 disabled:cursor-not-allowed',
            )}
          >
            {isLoading ? <Spinner className="w-5 h-5 animate-spin" /> : 'Найти'}
          </button>
        </div>

        {/* Suggestions */}
        {showSuggestions && suggestions.length > 0 && (
          <div className="absolute z-30 top-full mt-1.5 w-full rounded-xl bg-[var(--surface-primary)] border border-[var(--border-default)] shadow-xl overflow-hidden max-h-52 overflow-y-auto animate-fade-in">
            {suggestions.map((s, i) => (
              <button
                key={i}
                type="button"
                onClick={() => handleSelectSuggestion(s)}
                className="w-full px-4 py-2.5 text-left text-sm text-[var(--text-primary)] hover:bg-[var(--surface-hover)] transition-colors duration-100 flex items-start gap-2.5"
              >
                <MapPin weight="fill" className="w-4 h-4 text-[var(--primary)] mt-0.5 shrink-0" />
                <span className="line-clamp-2">{s.value}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Result */}
      {result && (
        <div
          className={cn(
            'mt-3 rounded-xl p-4 border animate-fade-in',
            result.isInZone
              ? 'bg-[var(--success-bg)] border-[var(--success)]/20'
              : 'bg-[var(--error-bg)] border-[var(--error)]/20',
          )}
        >
          <div className="flex items-start gap-2.5">
            {result.isInZone ? (
              <CheckCircle weight="fill" className="w-5 h-5 text-[var(--success)] shrink-0 mt-0.5" />
            ) : (
              <XCircle weight="fill" className="w-5 h-5 text-[var(--error)] shrink-0 mt-0.5" />
            )}
            <div className="min-w-0">
              <p className={cn('font-bold text-sm', result.isInZone ? 'text-[var(--success)]' : 'text-[var(--error)]')}>
                {result.isInZone ? 'В зоне доставки' : 'Вне зоны доставки'}
              </p>
              {result.isInZone ? (
                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs text-[var(--text-secondary)]">
                  <span>Доставка: <span className="font-bold text-[var(--text-primary)]">{result.deliveryCost} ₽</span></span>
                  <span>Время: <span className="font-bold text-[var(--text-primary)]">{result.estimatedTime}</span></span>
                  <span>Мин. заказ: <span className="font-bold text-[var(--text-primary)]">{result.minOrderSum} ₽</span></span>
                </div>
              ) : (
                <p className="text-xs text-[var(--text-secondary)] mt-1">
                  {result.message || 'Мы пока не доставляем сюда. Попробуйте другой адрес.'}
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );

  return (
    <section className="py-8 sm:py-12">
      {/* Карта монтируется ОДИН раз на оба брейкпоинта.
          Раньше здесь было два <YandexMapInner> (мобильная и десктопная вёрстки
          прятались только через CSS, но обе оставались в дереве). Каждый из них
          поднимает свой <YMaps>, а тот безусловно вставляет ещё один
          api-maps.yandex.ru с тем же namespace — Яндекс на второй отвечает
          «api is already enabled on this page with same namespace», и карта не
          инициализируется. Различается теперь только высота и место панели поиска. */}
      <div className="px-4 lg:max-w-7xl lg:mx-auto lg:px-6">
        <div className="relative rounded-2xl overflow-hidden h-[70vh] lg:h-[80vh] lg:min-h-[560px]">
          <YandexMapInner deliveryPoints={deliveryPoints} />

          {/* Desktop: search panel — bottom center overlay */}
          <div className="hidden lg:block absolute bottom-6 left-1/2 -translate-x-1/2 w-full max-w-[560px] z-20 rounded-2xl bg-[var(--surface-primary)]/95 border border-[var(--border-default)] p-5 shadow-2xl">
            {searchPanel}
          </div>
        </div>

        {/* Mobile: search panel below the map */}
        <div className="lg:hidden mt-4 rounded-2xl bg-[var(--surface-primary)] border border-[var(--border-default)] p-4 shadow-lg">
          <h3 className="text-base font-bold text-[var(--text-primary)] mb-3">
            Проверить адрес доставки
          </h3>
          {searchPanel}
        </div>
      </div>
    </section>
  );
}
